import React, { useEffect, useState } from 'react';

import reloadWhenSafe from 'reloadWhenSafe';

import usePlaylistPlayback from './usePlaylistPlayback';
import useWallboardManifest from './useWallboardManifest';
import WallboardPlayer from './WallboardPlayer';

const SIGN_OUT_VISIBLE_MS = 5000;
// Images don't leak memory the way the live app did (it reloaded every 10
// minutes), but reload daily to pick up new app versions.
const DAILY_RELOAD_MS = 24 * 60 * 60 * 1000;
const CLOCK_TICK_MS = 60 * 1000;

const useViewportSize = () => {
  const [size, setSize] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));
  useEffect(() => {
    const onResize = () => {
      setSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);
  return size;
};

// Re-render every minute so the stale banner appears without a new manifest.
const useNow = () => {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => {
      clearInterval(interval);
    };
  }, []);
  return now;
};

const useIsSignOutVisible = () => {
  const [isVisible, setIsVisible] = useState<boolean>(true);
  useEffect(() => {
    let timeout = setTimeout(() => setIsVisible(false), SIGN_OUT_VISIBLE_MS);
    const onMouseMove = () => {
      setIsVisible(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => setIsVisible(false), SIGN_OUT_VISIBLE_MS);
    };
    window.addEventListener('mousemove', onMouseMove);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, []);
  return isVisible;
};

const ReadyPlayer = ({
  manifest,
  viewport,
  now,
  isSignOutVisible,
}: {
  manifest: WallboardScreenshots.ManifestResponse;
  viewport: { width: number; height: number };
  now: number;
  isSignOutVisible: boolean;
}) => {
  const { item, previousUrl, advance, playKey } = usePlaylistPlayback({
    manifest,
  });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Space') {
        advance();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [advance]);

  return (
    <WallboardPlayer
      manifestState={{ status: 'ready', manifest }}
      item={item}
      previousUrl={previousUrl}
      playKey={playKey}
      viewport={viewport}
      now={now}
      isSignOutVisible={isSignOutVisible}
    />
  );
};

/**
 * A screenshot wallboard TV (wallboardScreenshots gate). It has no data access:
 * it plays the screenshots the renderer captures hourly, which it gets (with
 * signed URLs) from GET /v1/wallboards/manifest. Mounted by Navigation in place
 * of the whole authenticated app for wallboardTv sessions.
 */
const WallboardPlayerContainer = () => {
  const manifestState = useWallboardManifest();
  const viewport = useViewportSize();
  const now = useNow();
  const isSignOutVisible = useIsSignOutVisible();

  useEffect(() => {
    const timeout = setTimeout(() => {
      reloadWhenSafe();
    }, DAILY_RELOAD_MS);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  if (manifestState.status === 'ready') {
    return (
      <ReadyPlayer
        manifest={manifestState.manifest}
        viewport={viewport}
        now={now}
        isSignOutVisible={isSignOutVisible}
      />
    );
  }

  return (
    <WallboardPlayer
      manifestState={manifestState}
      item={undefined}
      viewport={viewport}
      now={now}
      isSignOutVisible={isSignOutVisible}
    />
  );
};

export default WallboardPlayerContainer;
